/**
 * Connectivity Monitor — module-level store (React-free) that pings the API
 * server every few seconds while the portal cannot reach it, and diagnoses
 * WHY, so the UI can explain the situation instead of guessing:
 *
 *   X — `server-down`  The SERVER did not answer, but the INTERNET works.
 *                      The server is restarting / crashed; keep retrying.
 *   Y — `no-internet`  The SERVER did not answer AND the internet is
 *                      unreachable. The device itself is offline.
 *
 * Lifecycle:
 * - `startConnectivityMonitor()` is called by `NetworkErrorListener` (first
 *   `ERR_NETWORK` reported through `services/networkStatus`) and by
 *   `AuthContext` (auth bootstrap exhausted its retries). It is idempotent:
 *   a storm of parallel failures starts exactly ONE loop.
 * - While running it re-checks every `PING_INTERVAL_MS`; the first successful
 *   server ping settles the state back to `idle` and stops the loop — the
 *   overlay unmounts itself and the app resumes exactly where it was.
 *
 * Same module-store pattern as `hooks/usePrivacyMode.js`: a plain module with
 * a subscriber set, consumed through the `hooks/useConnectivity` hook.
 *
 * Why "any HTTP response" counts as UP: the ping targets the PUBLIC home
 * route (`server/routes/Home.js`, mounted at "/" — no auth, no DB). A
 * resolved fetch — 200, 404, even 500 — proves the server process answered,
 * so only a network failure/timeout counts as "server unreachable".
 */

// Ping cadence while reconnecting (Amine asked for a 2–4s loop).
export const PING_INTERVAL_MS = 3000;

// Abort a server ping that has not answered within this window.
export const PING_TIMEOUT_MS = 2500;

// External internet probes are a diagnosis hint only — fail them fast so a
// full check cycle stays well inside the ping cadence.
const INTERNET_PROBE_TIMEOUT_MS = 2000;

// Same base-URL derivation as `services/api.js` (keep the two in sync):
// REACT_APP_BASE_URL, else same-origin `/api`, else the local dev server.
const fallbackOrigin =
  typeof window !== "undefined" ? window.location.origin : "";
const API_BASE_URL = (
  process.env.REACT_APP_BASE_URL ||
  (fallbackOrigin && `${fallbackOrigin}/api`) ||
  "http://localhost:5001"
).replace(/\/$/, "");

const SERVER_PING_URL = `${API_BASE_URL}/`;

// Opaque probes used only to decide "does this device have internet?".
// Google first; Cloudflare as a fallback for networks that block Google.
const INTERNET_PROBE_URLS = [
  "https://www.google.com/generate_204",
  "https://1.1.1.1/cdn-cgi/trace",
];

let isRunning = false;
let isChecking = false;
let intervalId = null;

// Live state. `status` is one of: 'idle' | 'checking' | 'server-down' |
// 'no-internet'. Snapshots (copies) are handed to subscribers.
const state = {
  status: "idle",
  attempt: 0,
  lastCheckedAt: null,
};

const listeners = new Set();

/**
 * Hands a FRESH snapshot to every subscriber. A listener throwing must never
 * break the monitor (or the other listeners) — same defensive contract as the
 * old network bridge.
 *
 * Call sites are deliberate: state TRANSITIONS only (episode start, verdict
 * change, recovery, external stop) — never routine `attempt`/`lastCheckedAt`
 * ticks. Subscribers include `Layout` (near the app root), so notifying on
 * every tick re-rendered the entire app every `PING_INTERVAL_MS` for the
 * whole outage, and any consumer whose effect depends on theme/context
 * identity re-ran its fetch on each of those renders.
 */
const notifyListeners = () => {
  const snapshot = { ...state };

  listeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (err) {
      console.warn("ConnectivityMonitor: listener threw:", err);
    }
  });
};

/**
 * `fetch` with an abort-based timeout. Rejects on network failure, CORS
 * blocking, or timeout — exactly the "did not get through" signal we need.
 * `fetch` itself only rejects for those cases; HTTP error statuses resolve.
 */
const fetchWithTimeout = async (url, { timeoutMs, ...options }) => {
  if (typeof fetch !== "function") {
    throw new Error("fetch is unavailable");
  }

  // Every browser the portal supports has AbortController; if one somehow
  // does not, the request simply has no timeout instead of crashing.
  const controller =
    typeof AbortController === "function" ? new AbortController() : null;
  const timer =
    controller && timeoutMs
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;

  try {
    return await fetch(url, {
      cache: "no-store",
      ...options,
      signal: controller ? controller.signal : undefined,
    });
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
};

/**
 * True when the SERVER answered (any status code — see module header).
 * Never throws.
 */
const pingServer = async () => {
  try {
    await fetchWithTimeout(SERVER_PING_URL, {
      method: "GET",
      timeoutMs: PING_TIMEOUT_MS,
    });
    return true;
  } catch {
    return false;
  }
};

/**
 * True when the DEVICE can reach the internet. Never throws.
 * - `navigator.onLine === false` is a cheap, reliable negative hint: trust it
 *   and skip the probes entirely.
 * - Probes run with `mode: "no-cors"`: a RESOLVED opaque response means the
 *   request left the device and came back — exactly the signal we need.
 *   One fallback URL is tried before concluding "no internet" (some networks
 *   block Google).
 */
const pingInternet = async () => {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return false;
  }

  for (const url of INTERNET_PROBE_URLS) {
    try {
      await fetchWithTimeout(url, {
        method: "GET",
        mode: "no-cors",
        timeoutMs: INTERNET_PROBE_TIMEOUT_MS,
      });
      return true;
    } catch {
      // Probe blocked or failed — try the next one before giving up.
    }
  }

  return false;
};

/** Clears the loop WITHOUT touching the state (used by the recovery path,
 *  which owns the `idle` transition). */
const stopLoop = () => {
  isRunning = false;
  if (intervalId !== null) {
    clearInterval(intervalId);
    intervalId = null;
  }
};

/**
 * One full diagnosis: ping the server; if it answers → recovered (`idle`,
 * loop stops). If it does not → ping the internet and settle on the X/Y
 * verdict. Bumps `attempt` and `lastCheckedAt` on every completed failure so
 * the UI can show the user that checks are still happening — but subscribers
 * are NOT notified for those routine ticks (only a real verdict change
 * notifies); the overlay polls the live snapshot itself.
 *
 * Re-entrancy guard: ticks that land while a check is already in flight
 * (or after the loop stopped) are ignored.
 */
const checkOnce = async () => {
  if (!isRunning || isChecking) return;
  isChecking = true;

  try {
    const serverUp = await pingServer();

    if (serverUp) {
      // Recovered: settle the state FIRST, then stop the loop, then notify —
      // subscribers must never observe "stopped but still reconnecting".
      state.status = "idle";
      state.attempt = 0;
      state.lastCheckedAt = Date.now();
      stopLoop();
      notifyListeners();
      return;
    }

    // The server did not answer — diagnose the cause. The previous verdict
    // (if any) stays on screen while re-checking so the overlay heading does
    // not flicker between "server down" and "checking" on every tick; only
    // the very first check shows 'checking'.
    const internetUp = await pingInternet();
    const previousStatus = state.status;

    state.status = internetUp ? "server-down" : "no-internet";
    state.attempt += 1;
    state.lastCheckedAt = Date.now();

    // Notify ONLY when the verdict actually changed. A routine tick must not
    // wake the app (see `notifyListeners` above); the overlay keeps its
    // attempt counter live with its own local timer instead.
    if (state.status !== previousStatus) {
      notifyListeners();
    }
  } catch (err) {
    // The diagnosis must never throw into the interval callback.
    console.warn("ConnectivityMonitor: check failed:", err);
  } finally {
    isChecking = false;
  }
};

/**
 * Starts the monitor (idempotent — a no-op while it is already running).
 * Shows 'checking' immediately, runs the first diagnosis right away, then
 * repeats every `PING_INTERVAL_MS` until the server answers again.
 */
export const startConnectivityMonitor = () => {
  if (isRunning) return;

  isRunning = true;
  state.status = "checking";
  state.attempt = 0;
  state.lastCheckedAt = null;
  notifyListeners();

  checkOnce();
  intervalId = setInterval(checkOnce, PING_INTERVAL_MS);
};

/**
 * Stops the monitor and settles back to `idle` (so the overlay can never get
 * stuck if monitoring is stopped externally). The automatic recovery path
 * stops the loop from inside `checkOnce` and does not need this.
 */
export const stopConnectivityMonitor = () => {
  stopLoop();

  if (state.status !== "idle") {
    state.status = "idle";
    state.attempt = 0;
    notifyListeners();
  }
};

/**
 * Runs a diagnosis immediately instead of waiting for the next tick — used by
 * the overlay's "Retry now" button and by `notifyRequestSuccess` (a
 * successful request is strong evidence the server is back). No-op while a
 * check is already in flight or the monitor is stopped.
 */
export const retryConnectivityNow = () => {
  if (!isRunning) return;
  checkOnce();
};

/** Live snapshot: `{ status, attempt, lastCheckedAt }`. */
export const getConnectivityState = () => ({ ...state });

/** True while the monitor is running (i.e. a reconnect episode is on screen).
 *  Read by `services/networkStatus` to expose the outage flag to
 *  `ContextProvider`'s routine-dialog suppression. */
export const isConnectivityMonitorActive = () => isRunning;

/**
 * Subscribes to state changes and IMMEDIATELY delivers the current snapshot
 * (closes the subscribe race where a recovery lands between a consumer's
 * render and its effect). Returns the unsubscribe function.
 */
export const subscribeConnectivity = (listener) => {
  if (typeof listener !== "function") return () => {};

  listeners.add(listener);

  try {
    listener(getConnectivityState());
  } catch (err) {
    console.warn("ConnectivityMonitor: listener threw:", err);
  }

  return () => {
    listeners.delete(listener);
  };
};
